import type { AdminLearningContentSubCategory } from '../types/domain';

const STORAGE_KEY = 'cp.adminLearningContent.loginSync.v1';

/** 로그인 동안 유지. 새로고침·페이지 이동 후에도 유지하고, 로그아웃·재로그인 시 비운다. */
const sessionDbSyncedKeys = new Set<string>();
let hydrated = false;

function rowsKey(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): string {
  return `rows:${gradeNumber}:${subCategory}:${templateName.trim()}`;
}

function templatesKey(gradeNumber: number, subCategory: AdminLearningContentSubCategory): string {
  return `templates:${gradeNumber}:${subCategory}`;
}

function hydrateFromStorage(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { keys?: unknown };
    if (!Array.isArray(parsed.keys)) return;
    for (const key of parsed.keys) {
      if (typeof key === 'string' && key.trim()) sessionDbSyncedKeys.add(key);
    }
  } catch {
    /* ignore */
  }
}

function persistToStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ keys: Array.from(sessionDbSyncedKeys) })
    );
  } catch {
    /* quota */
  }
}

export function isAdminLearningContentRowsSessionDbSynced(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): boolean {
  if (!templateName.trim()) return false;
  hydrateFromStorage();
  return sessionDbSyncedKeys.has(rowsKey(gradeNumber, subCategory, templateName));
}

export function markAdminLearningContentRowsSessionDbSynced(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): void {
  if (!templateName.trim()) return;
  hydrateFromStorage();
  const key = rowsKey(gradeNumber, subCategory, templateName);
  if (sessionDbSyncedKeys.has(key)) return;
  sessionDbSyncedKeys.add(key);
  persistToStorage();
}

export function isAdminLearningContentTemplatesSessionDbSynced(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory
): boolean {
  hydrateFromStorage();
  return sessionDbSyncedKeys.has(templatesKey(gradeNumber, subCategory));
}

export function markAdminLearningContentTemplatesSessionDbSynced(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory
): void {
  hydrateFromStorage();
  const key = templatesKey(gradeNumber, subCategory);
  if (sessionDbSyncedKeys.has(key)) return;
  sessionDbSyncedKeys.add(key);
  persistToStorage();
}

/** 로그인·로그아웃 시 호출 — 다음 로그인 후 첫 조회에서 head/풀 조회를 다시 한다 (IDB 본문은 유지) */
export function clearAdminLearningContentSessionDbSync(): void {
  sessionDbSyncedKeys.clear();
  hydrated = true;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
